export interface ContentItem {
    title?: string;
    text?: string;
    content?: ContentItem[];
    pinned?: boolean;
    hidden?: boolean;
}

export interface ExportSelection {
    cv: boolean;
    about: boolean;
    aboutContacts: boolean;
    aboutWhoIAm: boolean;
    aboutEducation: boolean;
    aboutSkills: boolean;
    opensource: boolean;
    blog: boolean;
    projects: boolean;
    projects2020: boolean;
    projects2015: boolean;
    projects2010: boolean;
    projects2000: boolean;
}

export type PdfStyle = 'professional' | 'compact' | 'classic';

export const DEFAULT_SELECTION: ExportSelection = {
    cv: false,
    about: true,
    aboutContacts: true,
    aboutWhoIAm: true,
    aboutEducation: true,
    aboutSkills: true,
    opensource: true,
    blog: true,
    projects: true,
    projects2020: true,
    projects2015: true,
    projects2010: true,
    projects2000: true,
};

// ── Filtering ─────────────────────────────────────────────────────────────────

export function filterContent(
    content: ContentItem[],
    sel: ExportSelection,
    techKeywords: string[]
): ContentItem[] {
    const kws = techKeywords.map((k) => k.trim().toLowerCase()).filter(Boolean);
    const result: ContentItem[] = [];

    for (const item of content) {
        const title = (item.title || '').toLowerCase();

        if (title === 'curriculum vitae') {
            if (sel.cv) result.push(item);
        } else if (title === 'about') {
            if (sel.about) {
                const children = filterAbout(item.content || [], sel);
                if (children.length) result.push({ ...item, content: children });
            }
        } else if (title === 'main open source collaborations') {
            if (sel.opensource) {
                const entries = kws.length
                    ? filterByTech(item.content || [], kws)
                    : item.content || [];
                if (entries.length) result.push({ ...item, content: entries });
            }
        } else if (title === 'blog posts') {
            if (sel.blog) result.push(item);
        } else if (title === 'projects') {
            if (sel.projects) {
                const groups = filterProjectGroups(item.content || [], sel, kws);
                if (groups.length) result.push({ ...item, content: groups });
            }
        }
    }

    return result;
}

function filterAbout(children: ContentItem[], sel: ExportSelection): ContentItem[] {
    return children.filter((child) => {
        const t = (child.title || '').toLowerCase();
        if (t === 'contacts') return sel.aboutContacts;
        if (t === 'who i am') return sel.aboutWhoIAm;
        if (t === 'education') return sel.aboutEducation;
        if (t === 'skills') return sel.aboutSkills;
        return true;
    });
}

function filterProjectGroups(
    groups: ContentItem[],
    sel: ExportSelection,
    kws: string[]
): ContentItem[] {
    const groupEnabled: Record<string, boolean> = {
        '2020..': sel.projects2020,
        '2015-2019': sel.projects2015,
        '2010-2014': sel.projects2010,
        '2000-2009': sel.projects2000,
    };

    return groups
        .filter((g) => groupEnabled[g.title || ''])
        .map((g) => {
            const entries = kws.length
                ? filterByTech(g.content || [], kws)
                : g.content || [];
            return { ...g, content: entries };
        })
        .filter((g) => (g.content || []).length > 0);
}

function filterByTech(items: ContentItem[], kws: string[]): ContentItem[] {
    return items.filter((item) => {
        const text = (item.text || '').toLowerCase();
        return kws.some((kw) => text.includes(kw));
    });
}

// ── Markdown helpers ──────────────────────────────────────────────────────────

export function parseMarkdownTable(text: string): Array<[string, string]> {
    const rows: Array<[string, string]> = [];
    for (const line of text.split('\n')) {
        if (/^\|[\s:|-]+\|/.test(line)) continue; // separator row
        const m = line.match(/^\|\s*(.*?)\s*\|\s*(.*?)\s*\|?\s*$/);
        if (m && m[1] && m[2]) rows.push([m[1], m[2]]);
    }
    return rows;
}

function stripMarkdown(text: string): string {
    return text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [text](url) → text
        .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold** → bold
        .replace(/(?<![*])\*([^*]+)\*(?![*])/g, '$1') // *italic* → italic
        .replace(/`([^`]+)`/g, '$1'); // `code` → code
}

// ── PDF generation ────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DocContent = any[];

export function toPdfmakeDefinition(
    content: ContentItem[],
    style: PdfStyle,
    photoDataUrl?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
    if (style === 'classic') return toPdfmakeClassic(content, photoDataUrl);

    const compact = style === 'compact';
    const baseFontSize = compact ? 9 : 10;
    const sectionFontSize = compact ? 12 : 14;
    const subFontSize = compact ? 10 : 11;
    const sectionSpaceBefore = compact ? 8 : 14;

    const styles = {
        name: { fontSize: compact ? 18 : 22, bold: true, color: '#1a1a2e' },
        sectionHeader: {
            fontSize: sectionFontSize,
            bold: true,
            color: '#2980b9',
            marginTop: sectionSpaceBefore,
            marginBottom: 4,
        },
        subHeader: {
            fontSize: subFontSize,
            bold: true,
            color: '#2c3e50',
            marginTop: compact ? 4 : 6,
            marginBottom: 2,
        },
        subSubHeader: {
            fontSize: baseFontSize,
            bold: true,
            color: '#555',
            marginTop: 2,
            marginBottom: 1,
        },
        tableKey: { fontSize: baseFontSize - 1, color: '#666', bold: true },
        tableValue: { fontSize: baseFontSize },
        body: { fontSize: baseFontSize, color: '#222' },
        small: { fontSize: baseFontSize - 1, color: '#555' },
    };

    const body: DocContent = [];

    for (const section of content) {
        renderItem(section, body, 0, compact, baseFontSize);
        body.push({ text: '', margin: [0, compact ? 4 : 6, 0, 0] });
    }

    return {
        content: body,
        styles,
        pageMargins: compact ? [30, 25, 30, 25] : [40, 35, 40, 35],
        defaultStyle: { font: 'Roboto', fontSize: baseFontSize, lineHeight: 1.3 },
    };
}

function renderItem(
    item: ContentItem,
    output: DocContent,
    depth: number,
    compact: boolean,
    baseFontSize: number
): void {
    if (item.hidden) return;

    if (item.title) {
        const styleMap = ['sectionHeader', 'subHeader', 'subSubHeader'];
        output.push({ text: item.title, style: styleMap[Math.min(depth, 2)] });
    }

    if (item.text) {
        const isTable = item.text.includes('|') && item.text.includes('\n');
        if (isTable) {
            const rows = parseMarkdownTable(item.text);
            if (rows.length) {
                output.push(renderProjectTable(rows, compact, baseFontSize));
                return;
            }
        }
        // Plain markdown text — split into paragraphs
        const paragraphs = item.text
            .split('\n\n')
            .map((p) => p.replace(/\n/g, ' ').trim())
            .filter(Boolean);
        for (const p of paragraphs) {
            output.push({
                text: stripMarkdown(p),
                style: 'body',
                margin: [0, 1, 0, compact ? 2 : 3],
            });
        }
    }

    if (item.content) {
        // Skills leaf lists: when all children are text-only leaves, render inline
        const allLeaves = item.content.every((c) => !c.content && c.text && !c.title);
        if (allLeaves && item.content.length > 0) {
            const joined = item.content.map((c) => c.text || '').join(', ');
            output.push({
                text: joined,
                style: 'small',
                margin: [0, 0, 0, compact ? 2 : 3],
            });
            return;
        }

        for (const child of item.content) {
            renderItem(child, output, depth + 1, compact, baseFontSize);
        }
    }
}

function renderProjectTable(
    rows: Array<[string, string]>,
    compact: boolean,
    baseFontSize: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
    const tableRows = rows.map(([key, value]) => [
        { text: key, style: 'tableKey', noWrap: false },
        { text: stripMarkdown(value), style: 'tableValue' },
    ]);

    return {
        table: {
            widths: [compact ? 75 : 90, '*'],
            body: tableRows,
        },
        layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0,
            hLineColor: () => '#ddd',
            paddingTop: () => (compact ? 2 : 3),
            paddingBottom: () => (compact ? 2 : 3),
            paddingLeft: (i: number) => (i === 0 ? 0 : 6),
            paddingRight: () => 0,
        },
        margin: [0, 2, 0, compact ? 6 : 10],
        fontSize: baseFontSize,
    };
}

// ── Classic style (mirrors the official PDF layout) ───────────────────────────

function toPdfmakeClassic(
    content: ContentItem[],
    photoDataUrl?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
    const fs = 10;

    const styles = {
        docTitle: {
            fontSize: 24,
            bold: false,
            color: '#111',
            margin: [0, 0, 0, 24],
        },
        sectionHeader: {
            fontSize: 13,
            bold: true,
            color: '#111',
            marginTop: 16,
            marginBottom: 4,
        },
        subHeader: {
            fontSize: fs + 1,
            bold: true,
            color: '#222',
            marginTop: 10,
            marginBottom: 3,
        },
        subSubHeader: {
            fontSize: fs,
            bold: true,
            color: '#444',
            marginTop: 4,
            marginBottom: 1,
        },
        body: { fontSize: fs, color: '#222', lineHeight: 1.4 },
        small: { fontSize: fs - 1, color: '#555' },
        bulletKey: { fontSize: fs, bold: true, color: '#111' },
        bulletVal: { fontSize: fs, color: '#222' },
    };

    const body: DocContent = [];

    body.push({ text: 'Curriculum Vitae — Roberto Previdi', style: 'docTitle' });

    for (const section of content) {
        renderClassicItem(section, body, 0, fs, photoDataUrl);
        body.push({ text: '', marginBottom: 6 });
    }

    return {
        content: body,
        styles,
        pageMargins: [50, 40, 50, 40],
        defaultStyle: { font: 'Roboto', fontSize: fs, lineHeight: 1.35 },
    };
}

function renderClassicItem(
    item: ContentItem,
    output: DocContent,
    depth: number,
    fs: number,
    photoDataUrl?: string
): void {
    if (item.hidden) return;

    if (item.title) {
        if (depth === 0) {
            // Section header + thin horizontal rule
            output.push({ text: item.title, style: 'sectionHeader' });
            output.push({
                canvas: [
                    {
                        type: 'line',
                        x1: 0,
                        y1: 0,
                        x2: 515,
                        y2: 0,
                        lineWidth: 0.5,
                        lineColor: '#aaa',
                    },
                ],
                margin: [0, 0, 0, 6],
            });
        } else if (depth === 1) {
            output.push({ text: item.title, style: 'subHeader' });
        } else {
            output.push({ text: item.title, style: 'subSubHeader' });
        }
    }

    if (item.text) {
        // Skip image markdown — rendered separately via photoDataUrl
        if (/^!\[/.test(item.text)) return;

        const isTable = item.text.includes('|') && item.text.includes('\n');
        if (isTable) {
            const rows = parseMarkdownTable(item.text);
            if (rows.length) {
                output.push(renderClassicBullets(rows, fs));
                return;
            }
        }
        const paragraphs = item.text
            .split('\n\n')
            .map((p) => p.replace(/\n/g, ' ').trim())
            .filter(Boolean);
        for (const p of paragraphs) {
            output.push({ text: stripMarkdown(p), style: 'body', marginBottom: 4 });
        }
    }

    if (item.content) {
        // Contacts: special 2-col layout with photo
        if (item.title?.toLowerCase() === 'contacts' && depth >= 1) {
            renderClassicContacts(item.content, output, fs, photoDataUrl);
            return;
        }

        // Skill leaf lists: comma-joined
        const allLeaves = item.content.every((c) => !c.content && c.text && !c.title);
        if (allLeaves && item.content.length > 0) {
            const joined = item.content.map((c) => c.text || '').join(', ');
            output.push({ text: joined, style: 'small', marginBottom: 3 });
            return;
        }

        for (const child of item.content) {
            renderClassicItem(child, output, depth + 1, fs, photoDataUrl);
        }
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderClassicBullets(rows: Array<[string, string]>, fs: number): any {
    const bullets = rows
        .filter(([k]) => !['project', 'company'].includes(k.toLowerCase()))
        .map(([key, value]) => ({
            columns: [
                { text: `${key}:`, bold: true, fontSize: fs, width: 90, color: '#333' },
                { text: stripMarkdown(value), fontSize: fs, color: '#222' },
            ],
            columnGap: 6,
            margin: [0, 0, 0, 2],
        }));
    return { stack: bullets, margin: [0, 2, 0, 10] };
}

function renderClassicContacts(
    children: ContentItem[],
    output: DocContent,
    fs: number,
    photoDataUrl?: string
): void {
    const textChildren = children.filter((c) => !/^!\[/.test(c.text || ''));
    const textStack: DocContent = [];

    for (const child of textChildren) {
        if (child.text) {
            const paragraphs = child.text
                .split('\n\n')
                .map((p) => p.replace(/\n/g, ' ').trim())
                .filter(Boolean);
            for (const p of paragraphs) {
                textStack.push({
                    text: stripMarkdown(p),
                    fontSize: fs,
                    marginBottom: 3,
                });
            }
        }
    }

    if (photoDataUrl) {
        output.push({
            table: {
                widths: [110, '*'],
                body: [
                    [
                        { image: photoDataUrl, width: 100, margin: [0, 0, 8, 0] },
                        { stack: textStack, margin: [6, 0, 0, 0] },
                    ],
                ],
            },
            layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#ccc',
                vLineColor: () => '#ccc',
                paddingLeft: () => 8,
                paddingRight: () => 8,
                paddingTop: () => 8,
                paddingBottom: () => 8,
            },
            margin: [0, 0, 0, 12],
        });
    } else {
        output.push({ stack: textStack, margin: [0, 0, 0, 10] });
    }
}

// ── Markdown export ───────────────────────────────────────────────────────────

export function toMarkdown(content: ContentItem[], depth = 0): string {
    let out = '';

    for (const item of content) {
        if (item.hidden) continue;

        if (item.title) {
            const level = Math.min(depth + 1, 4);
            const hashes = '#'.repeat(level);
            if (depth === 0) out += '\n---\n\n';
            out += `${hashes} ${item.title}\n\n`;
        }

        if (item.text) {
            out += `${item.text}\n\n`;
        }

        if (item.content) {
            out += toMarkdown(item.content, depth + 1);
        }
    }

    return out.trimStart();
}
